import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import API from 'api/API';
import { DraftSatoSetResponse, OnboardingStatus } from 'api/Responses/SatoResponses';
import { SortStatus } from 'api/Responses/SetResponses';
import { DATE_PROPERTIES } from 'utils/helpers/constants';
import { getSubmissionDate } from 'utils/helpers/SATO.helpers';

export type StatusFilter = OnboardingStatus | 'all';

interface UseSatoSets {
  allDrafts: DraftSatoSetResponse[];
  filteredDrafts: DraftSatoSetResponse[];
  loading: boolean;
  filter: StatusFilter;
  setFilter: Dispatch<SetStateAction<StatusFilter>>;
  sort: SortStatus;
  setSort: Dispatch<SetStateAction<SortStatus>>;
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
}

const useSatoSets = (): UseSatoSets => {
  const [allDrafts, setAllDrafts] = useState<DraftSatoSetResponse[]>([]);
  const [filteredDrafts, setFilteredDrafts] = useState<DraftSatoSetResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>(OnboardingStatus.PENDING);
  const [sort, setSort] = useState<SortStatus>(SortStatus.OLDEST);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Filters the drafts depending on the state of the filter
  const filteredSets = useMemo(() => {
    if (filter === 'all') {
      return filteredDrafts;
    } else {
      return filteredDrafts.filter(draft => draft.status === filter);
    }
  }, [filteredDrafts, filter]);

  // Filters the drafts depending on the state of the search query using regex
  const searchedSets = useMemo(() => {
    const regexSearchQuery = new RegExp(searchQuery, 'i');
    if (searchQuery.length >= 3) {
      return filteredSets.filter(draft => draft.code.search(regexSearchQuery) !== -1);
    } else {
      return filteredSets;
    }
  }, [filteredSets, searchQuery]);

  // Sort the drafts depending on the state of the sort
  const sortSets = useMemo(() => {
    return searchedSets.sort((a, b) => {
      const defaultDate = sort === SortStatus.NEWEST ? DATE_PROPERTIES.MAX_VALUE : DATE_PROPERTIES.MIN_VALUE;
      const submissionDateA = new Date(getSubmissionDate(a) ?? defaultDate);
      const submissionDateB = new Date(getSubmissionDate(b) ?? defaultDate);

      if (sort === SortStatus.NEWEST) {
        return submissionDateB.getTime() - submissionDateA.getTime();
      }

      if (sort === SortStatus.OLDEST) {
        return submissionDateA.getTime() - submissionDateB.getTime();
      }

      return 0;
    });
  }, [searchedSets, sort]);

  // Fetch all drafts from the DB
  useEffect(() => {
    (async () => {
      try {
        const _drafts = await API.sato.getAllDrafts();
        setFilteredDrafts(_drafts);
        setAllDrafts(_drafts);
      } catch (error) {
        API.errorHandler(error);
      }
      setLoading(false);
    })();
  }, []);

  return {
    loading,
    allDrafts,
    filteredDrafts: sortSets,
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    sort,
    setSort,
  };
};

export default useSatoSets;
